import { useState, useEffect } from "react";
import { X, ArrowUpFromLine, PlusSquare, Pencil, Check } from "lucide-react";

const DISMISS_KEY = "wagc_pwa_dismissed";
export const ENGAGE_KEY = "wagc_engage_count";
export const ENGAGE_THRESHOLD = 3;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const IOS_STEPS = [
  {
    icon: ArrowUpFromLine,
    label: "Tap the Share button",
    sub: "The square with an up arrow at the bottom of Safari",
  },
  {
    icon: PlusSquare,
    label: 'Tap "Add to Home Screen"',
    sub: "Scroll down in the share sheet if needed",
  },
  {
    icon: Pencil,
    label: 'Rename to "Word Alive" if you like',
    sub: "Optional — keeps things tidy on your home screen",
  },
  {
    icon: Check,
    label: 'Tap "Add"',
    sub: "The app icon will appear on your home screen!",
  },
];

export function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const tryShow = () => {
      if (localStorage.getItem(DISMISS_KEY)) return;  // guard re-show after dismiss
      const count = parseInt(localStorage.getItem(ENGAGE_KEY) || "0", 10);
      if (count < ENGAGE_THRESHOLD) return;

      const ios = isIOS();
      const hasPrompt = !!window._wagcInstallPrompt;

      // iOS: show instruction banner. Android: only if native prompt available.
      if (!ios && !hasPrompt) return;

      if (hasPrompt) setDeferredPrompt(window._wagcInstallPrompt);
      setVisible(true);
    };

    tryShow();

    const onEngage = () => tryShow();
    const onPrompt = () => {
      if (window._wagcInstallPrompt) setDeferredPrompt(window._wagcInstallPrompt);
      tryShow();
    };

    window.addEventListener("wagc-engage", onEngage);
    window.addEventListener("wagc-installprompt", onPrompt);
    return () => {
      window.removeEventListener("wagc-engage", onEngage);
      window.removeEventListener("wagc-installprompt", onPrompt);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "true");
    setVisible(false);
  };

  const handleAddNow = async () => {
    if (isIOS()) {
      setIosOpen(true);
    } else if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      window._wagcInstallPrompt = null;
      setDeferredPrompt(null);
      if (outcome === "accepted") dismiss();
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* ===== Install Banner ===== */}
      <div
        data-testid="install-banner"
        className="w-full bg-navy border-b border-gold/50 shadow-sm px-4 py-3 flex items-center gap-3 animate-in slide-in-from-top-3 fade-in duration-300"
      >
        {/* App icon */}
        <img
          src="https://assets.wordaliveglobal.org/branding/icon-192.png"
          alt="Word Alive"
          className="w-10 h-10 rounded-xl shrink-0 object-cover shadow-sm"
        />

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold leading-tight font-heading">
            Add to Home Screen for easy access
          </p>
          <p className="text-white/70 text-xs mt-0.5 leading-tight">
            Opens like a real app – works offline too
          </p>
        </div>

        {/* Add Now button */}
        <button
          data-testid="install-add-now-btn"
          onClick={handleAddNow}
          className="bg-gold text-navy text-sm font-bold px-4 py-2 rounded-sm shrink-0 hover:bg-gold/90 active:scale-95 transition-all whitespace-nowrap"
        >
          Add Now
        </button>

        {/* Dismiss */}
        <button
          data-testid="install-dismiss-btn"
          onClick={dismiss}
          className="text-white/50 hover:text-white transition-colors shrink-0 -mr-1 ml-1"
          aria-label="Dismiss install banner"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* ===== iOS Instructions Modal ===== */}
      {iosOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center"
          data-testid="ios-install-modal"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIosOpen(false)}
          />

          {/* Sheet */}
          <div className="relative bg-white w-full max-w-md rounded-t-2xl px-6 pt-5 pb-8 animate-in slide-in-from-bottom-3 duration-300">
            {/* Handle bar */}
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading text-navy text-lg font-semibold leading-tight">
                  Add to Home Screen
                </h3>
                <p className="text-gray-400 text-xs mt-0.5">
                  Follow these 4 quick steps in Safari
                </p>
              </div>
              <button
                onClick={() => setIosOpen(false)}
                className="text-gray-300 hover:text-gray-500 transition-colors ml-4"
                aria-label="Close"
                data-testid="ios-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Steps */}
            <div className="space-y-5">
              {IOS_STEPS.map(({ icon: Icon, label, sub }, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  {/* Step number circle */}
                  <div className="w-9 h-9 rounded-full bg-navy flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-gold" />
                  </div>
                  <div className="flex-1 min-w-0 pt-1">
                    <p className="text-navy text-sm font-semibold leading-snug">
                      {label}
                    </p>
                    {sub && (
                      <p className="text-gray-400 text-xs mt-0.5 leading-snug">
                        {sub}
                      </p>
                    )}
                  </div>
                  {/* Step counter */}
                  <span className="text-xs text-gray-300 font-bold shrink-0 pt-1.5">
                    {idx + 1}/{IOS_STEPS.length}
                  </span>
                </div>
              ))}
            </div>

            {/* Close button */}
            <button
              className="mt-7 w-full bg-navy text-white text-sm font-semibold py-3 rounded-sm hover:bg-navy/90 transition-colors"
              onClick={() => setIosOpen(false)}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
}
